import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

/**
 * Report Download Controller
 *
 * Intercepts download clicks to save the report first, then triggers the download.
 * Works by handling the click event directly and performing a fetch request for the JS response.
 *
 * Usage:
 *   = link_to year_report_path(@year, @report),
 *       data: { controller: 'report-download', action: 'click->report-download#download' } do
 *     i.fa.fa-download
 */
export default class extends Controller {
  async download(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = this.element
    const icon = button.querySelector('i')
    const originalIconClass = icon ? icon.className : ''

    // Show saving indicator
    if (icon) icon.className = 'fa fa-spinner fa-spin'
    button.classList.add('disabled')

    // Find the autosave controller
    const autosaveElement = document.querySelector('[data-controller~="report-autosave"]')

    if (autosaveElement) {
      const autosaveController = this.application.getControllerForElementAndIdentifier(
        autosaveElement,
        'report-autosave'
      )

      if (autosaveController) {
        try {
          // Trigger autosave and wait for it to complete
          await autosaveController.performAutoSave()

          // Trigger the save button's success indicator (shows for 2 seconds)
          autosaveController.showSaveIndicator()

          // Wait for save indicator, then trigger download
          await new Promise(resolve => setTimeout(resolve, 2000))
        } catch (error) {
          logger.error('[Report Download] Error during save:', error)

          // Show error indicator
          if (icon) icon.className = 'fa fa-times text-danger'
          autosaveController.showErrorIndicator()

          await new Promise(resolve => setTimeout(resolve, 2000))
        }
      }
    }

    // Restore button state
    if (icon) icon.className = originalIconClass
    button.classList.remove('disabled')

    // Now perform the actual download via fetch
    await this.executeDownload(button.href)
  }

  async executeDownload(url) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/javascript, application/javascript, */*',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type') || ''

      if (contentType.includes('javascript')) {
        const script = await response.text()
        logger.log('[Report Download] Executing JS response')
        eval(script)
      }
    } catch (error) {
      console.error('[Report Download] Request failed:', error)
      this.showError()
    }
  }

  showError() {
    const flashContainer = document.getElementById('flash-container')
    if (flashContainer) {
      flashContainer.insertAdjacentHTML(
        'beforeend',
        `<div class="alert alert-danger alert-dismissible fade show" role="alert">
          שגיאה בהורדת הדו״ח. אנא נסה שוב.
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>`
      )
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
